import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import { trpc } from '@/api/trpc';
import { showToast } from '@/components/Layout/Toast';
import { Button, FormInput, Modal } from '@/components/UI';
import { useAuth } from '@/context/AuthContext';
import { formStyles } from '@/utils/formStyles';
import { SUPPORT_WRITE_SCOPE_OPTIONS, type SupportWriteScope } from '@/utils/supportWriteScopes';

interface StartSupportSessionModalProps {
    isOpen: boolean;
    onClose: () => void;
}

interface SupportSearchResult {
    id: number;
    email: string;
    type: number;
}

const emptySearchResults: SupportSearchResult[] = [];

const StartSupportSessionModal = ({ isOpen, onClose }: StartSupportSessionModalProps) => {
    const { registerSupportPasskey, startSupportSession, supportStepUp } = useAuth();

    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [selectedUser, setSelectedUser] = useState<SupportSearchResult | null>(null);
    const [reason, setReason] = useState('');
    const [requestedMode, setRequestedMode] = useState<'readOnly' | 'limitedWrite'>('readOnly');
    const [requestedWriteScopes, setRequestedWriteScopes] = useState<SupportWriteScope[]>([]);
    const [ticketReference, setTicketReference] = useState('');
    const [totpCode, setTotpCode] = useState('');
    const [isRegisteringPasskey, setIsRegisteringPasskey] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const resetForm = () => {
        setSearchTerm('');
        setDebouncedSearchTerm('');
        setSelectedUser(null);
        setReason('');
        setRequestedMode('readOnly');
        setRequestedWriteScopes([]);
        setTicketReference('');
        setTotpCode('');
        setIsRegisteringPasskey(false);
        setIsSubmitting(false);
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            setDebouncedSearchTerm(searchTerm.trim());
        }, 250);

        return () => window.clearTimeout(timeoutId);
    }, [isOpen, searchTerm]);

    const searchUsersQuery = useQuery({
        ...trpc.support.searchUsers.queryOptions({
            term: debouncedSearchTerm,
            limit: 8
        }),
        enabled: isOpen && debouncedSearchTerm.length >= 2,
        placeholderData: keepPreviousData
    });

    const searchResults = useMemo<SupportSearchResult[]>(() => {
        if (!searchUsersQuery.data) {
            return emptySearchResults;
        }

        return searchUsersQuery.data;
    }, [searchUsersQuery.data]);

    const isLimitedWrite = requestedMode === 'limitedWrite';
    const isPasskeyStepUp = supportStepUp?.requiredMethod === 'passkey';
    const hasClosePositionScope = requestedWriteScopes.includes('trading.closePosition.write');
    const passkeyCtaLabel = supportStepUp?.hasPasskey ? 'Register Another Passkey' : 'Register Passkey';

    const canSubmit = Boolean(
        selectedUser &&
        reason.trim().length >= 10 &&
        (isPasskeyStepUp || /^\d{6}$/.test(totpCode)) &&
        (!isLimitedWrite ||
            (ticketReference.trim().length > 0 && requestedWriteScopes.length > 0 && (!hasClosePositionScope || isPasskeyStepUp)))
    );

    const handleSubmit = async () => {
        if (!selectedUser) {
            showToast('Select a user before starting a support session.', 'warning');
            return;
        }

        if (reason.trim().length < 10) {
            showToast('Enter a support reason with at least 10 characters.', 'warning');
            return;
        }

        if (!isPasskeyStepUp && !/^\d{6}$/.test(totpCode)) {
            showToast('Enter a valid 6-digit TOTP code.', 'warning');
            return;
        }

        if (isLimitedWrite && !ticketReference.trim()) {
            showToast('Limited-write support sessions require a ticket reference.', 'warning');
            return;
        }

        if (isLimitedWrite && requestedWriteScopes.length === 0) {
            showToast('Select at least one write scope for a limited-write support session.', 'warning');
            return;
        }

        if (isLimitedWrite && hasClosePositionScope && !isPasskeyStepUp) {
            showToast('Close-position support sessions require an admin passkey.', 'warning');
            return;
        }

        try {
            setIsSubmitting(true);

            try {
                await startSupportSession({
                    targetUserId: selectedUser.id,
                    reason: reason.trim(),
                    requestedMode,
                    requestedWriteScopes,
                    ticketReference: ticketReference.trim() || null,
                    totpCode: isPasskeyStepUp ? undefined : totpCode
                });

                handleClose();
            } catch (error) {
                if (!(error instanceof Error)) {
                    showToast('Failed to start support session.', 'error');
                }
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleRegisterPasskey = async () => {
        try {
            setIsRegisteringPasskey(true);
            await registerSupportPasskey();
        } catch (error) {
            console.error('Failed to register support passkey:', error);
        } finally {
            setIsRegisteringPasskey(false);
        }
    };

    const footer = (
        <>
            <Button disabled={isSubmitting} variant="secondary" onClick={handleClose}>
                Cancel
            </Button>
            <Button disabled={!canSubmit} isLoading={isSubmitting} onClick={handleSubmit}>
                {isLimitedWrite ? 'Start Limited-Write Session' : 'Start Read-Only Session'}
            </Button>
        </>
    );

    return (
        <Modal footer={footer} isOpen={isOpen} size="lg" title="Start Support Session" onClose={handleClose}>
            <div className="space-y-5">
                <div
                    className={`rounded-lg px-4 py-3 text-sm leading-6 ${
                        isLimitedWrite
                            ? 'border border-rose-500/25 bg-rose-500/10 text-rose-100'
                            : 'border border-amber-500/25 bg-amber-500/10 text-amber-100'
                    }`}
                >
                    {isLimitedWrite
                        ? 'Limited-write support sessions are audited, scoped, and notify the customer by email when the session starts. Trading remains blocked except for the passkey-gated close-positions scope.'
                        : 'Read-only support sessions keep all mutations blocked. Admin routes, agent chat, brokerage connection management, and 2FA changes remain unavailable.'}
                </div>

                <div className="space-y-3">
                    <div className="text-sm font-medium text-zinc-200">Session Mode</div>
                    <div className="grid gap-3 md:grid-cols-2">
                        <button
                            className={`rounded-lg border px-4 py-3 text-left transition-colors ${
                                requestedMode === 'readOnly'
                                    ? 'border-amber-400/60 bg-amber-500/10 text-white'
                                    : 'border-zinc-800 bg-[#151515] text-zinc-300 hover:border-zinc-700 hover:bg-zinc-900'
                            }`}
                            type="button"
                            onClick={() => {
                                setRequestedMode('readOnly');
                                setRequestedWriteScopes([]);
                            }}
                        >
                            <div className="font-medium">Read-only</div>
                            <div className="mt-1 text-sm text-zinc-400">View the customer account without permitting any mutations.</div>
                        </button>
                        <button
                            className={`rounded-lg border px-4 py-3 text-left transition-colors ${
                                requestedMode === 'limitedWrite'
                                    ? 'border-rose-400/60 bg-rose-500/10 text-white'
                                    : 'border-zinc-800 bg-[#151515] text-zinc-300 hover:border-zinc-700 hover:bg-zinc-900'
                            }`}
                            type="button"
                            onClick={() => setRequestedMode('limitedWrite')}
                        >
                            <div className="font-medium">Limited write</div>
                            <div className="mt-1 text-sm text-zinc-400">Allow only the specific support mutations selected below.</div>
                        </button>
                    </div>
                </div>

                <div className="space-y-2">
                    <FormInput
                        autoComplete="off"
                        helpText="Search by the customer email address."
                        label="Target User"
                        placeholder="Start typing an email address"
                        value={searchTerm}
                        onChange={(event) => {
                            const nextValue = event.target.value;

                            setSearchTerm(nextValue);

                            if (selectedUser && nextValue.trim() !== selectedUser.email) {
                                setSelectedUser(null);
                            }
                        }}
                    />

                    {selectedUser ? (
                        <div className="rounded-lg border border-sky-500/25 bg-sky-500/10 px-4 py-3 text-sm text-sky-100">
                            Acting as <span className="font-medium">{selectedUser.email}</span>
                        </div>
                    ) : debouncedSearchTerm.length >= 2 ? (
                        <div className="rounded-lg border border-zinc-800 bg-[#151515]">
                            {searchUsersQuery.isLoading ? (
                                <div className="px-4 py-3 text-sm text-zinc-400">Searching users…</div>
                            ) : searchResults.length === 0 ? (
                                <div className="px-4 py-3 text-sm text-zinc-400">No matching non-admin users found.</div>
                            ) : (
                                <div className="max-h-64 overflow-y-auto py-1">
                                    {searchResults.map((result) => (
                                        <button
                                            className="flex w-full items-center justify-between px-4 py-3 text-left text-sm text-zinc-200 transition-colors hover:bg-zinc-800"
                                            key={result.id}
                                            type="button"
                                            onClick={() => {
                                                setSelectedUser(result);
                                                setSearchTerm(result.email);
                                            }}
                                        >
                                            <span>{result.email}</span>
                                            <span className="text-xs text-zinc-500">User #{result.id}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="text-sm text-zinc-500">Type at least two characters to search.</div>
                    )}
                </div>

                <div>
                    <label className={formStyles.label} htmlFor="support-session-reason">
                        Reason
                    </label>
                    <textarea
                        className={`${formStyles.textarea} ${formStyles.placeholder}`}
                        id="support-session-reason"
                        maxLength={500}
                        placeholder="Describe why this support session is needed."
                        value={reason}
                        onChange={(event) => setReason(event.target.value)}
                    />
                    <div className={formStyles.helpText}>This reason is stored in the support-session audit log.</div>
                </div>

                <FormInput
                    helpText={isLimitedWrite ? 'Required for limited-write support sessions.' : 'Optional ticket or incident reference.'}
                    label="Ticket Reference"
                    maxLength={100}
                    placeholder="INC-1234"
                    value={ticketReference}
                    onChange={(event) => setTicketReference(event.target.value)}
                />

                {isLimitedWrite && (
                    <div className="space-y-3">
                        <div className="text-sm font-medium text-zinc-200">Allowed Write Scopes</div>
                        <div className="space-y-2">
                            {SUPPORT_WRITE_SCOPE_OPTIONS.map((scopeOption) => {
                                const isSelected = requestedWriteScopes.includes(scopeOption.value);
                                const requiresPasskey = scopeOption.value === 'trading.closePosition.write';
                                const isDisabled = requiresPasskey && !isPasskeyStepUp;

                                return (
                                    <button
                                        className={`flex w-full items-start gap-3 rounded-lg border px-4 py-3 text-left transition-colors ${
                                            isSelected
                                                ? 'border-rose-400/50 bg-rose-500/10 text-white'
                                                : isDisabled
                                                  ? 'cursor-not-allowed border-zinc-800 bg-[#111111] text-zinc-500'
                                                  : 'border-zinc-800 bg-[#151515] text-zinc-300 hover:border-zinc-700 hover:bg-zinc-900'
                                        }`}
                                        disabled={isDisabled}
                                        key={scopeOption.value}
                                        type="button"
                                        onClick={() => {
                                            if (isDisabled) {
                                                return;
                                            }

                                            setRequestedWriteScopes((currentScopes) =>
                                                currentScopes.includes(scopeOption.value)
                                                    ? currentScopes.filter((scope) => scope !== scopeOption.value)
                                                    : [...currentScopes, scopeOption.value]
                                            );
                                        }}
                                    >
                                        <span
                                            className={`mt-0.5 flex h-5 w-5 items-center justify-center rounded border text-xs ${
                                                isSelected
                                                    ? 'border-rose-300 bg-rose-400/30 text-white'
                                                    : 'border-zinc-600 text-transparent'
                                            }`}
                                        >
                                            ✓
                                        </span>
                                        <span className="min-w-0">
                                            <span className="block font-medium">{scopeOption.label}</span>
                                            <span className="mt-1 block text-sm text-zinc-400">{scopeOption.description}</span>
                                            {isDisabled && (
                                                <span className="mt-1 block text-xs text-zinc-500">
                                                    Register an admin passkey before requesting this scope.
                                                </span>
                                            )}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                {isPasskeyStepUp ? (
                    <div className="space-y-3">
                        <div className="rounded-lg border border-emerald-500/25 bg-emerald-500/10 px-4 py-3 text-sm leading-6 text-emerald-100">
                            This admin account requires a passkey to approve support sessions. When you start the session, the browser will
                            prompt for passkey verification.
                        </div>
                        <div className="flex flex-col gap-3 rounded-lg border border-sky-500/20 bg-sky-500/10 px-4 py-3 md:flex-row md:items-center md:justify-between">
                            <div className="text-sm leading-6 text-sky-100">
                                Use this if this browser or device does not already have one of your registered passkeys available.
                            </div>
                            <Button
                                disabled={isSubmitting}
                                isLoading={isRegisteringPasskey}
                                size="sm"
                                variant="outline-dark"
                                onClick={handleRegisterPasskey}
                            >
                                {passkeyCtaLabel}
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-4">
                        <FormInput
                            autoComplete="one-time-code"
                            helpText="Fresh admin TOTP is required until a support passkey is registered."
                            label="Admin TOTP Code"
                            maxLength={6}
                            placeholder="123456"
                            value={totpCode}
                            onChange={(event) => setTotpCode(event.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' && canSubmit) {
                                    event.preventDefault();
                                    void handleSubmit();
                                }
                            }}
                        />

                        <div className="flex flex-col gap-3 rounded-lg border border-sky-500/20 bg-sky-500/10 px-4 py-3 md:flex-row md:items-center md:justify-between">
                            <div className="text-sm leading-6 text-sky-100">
                                Register a passkey to make future support-session approvals use WebAuthn instead of TOTP.
                            </div>
                            <Button
                                disabled={isSubmitting}
                                isLoading={isRegisteringPasskey}
                                size="sm"
                                variant="outline-dark"
                                onClick={handleRegisterPasskey}
                            >
                                {passkeyCtaLabel}
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default StartSupportSessionModal;
